/**
 * Odoo API utilities for interacting with shipment data
 */

// Configuration for Odoo API (direct connection and proxy)
const ODOO_API_URL = import.meta.env.VITE_ODOO_API_URL || 'https://technosun.odoo.com';
const ODOO_API_KEY = import.meta.env.VITE_ODOO_API_KEY || '';
const ODOO_DB = import.meta.env.VITE_ODOO_DB || 'octupustechnologies-technosun13-main-5564560';
const ODOO_USERNAME = import.meta.env.VITE_ODOO_USERNAME || 'toni@technosun.com';

// Proxy server URL - cambia esto a tu URL después del despliegue
const PROXY_URL = import.meta.env.VITE_PROXY_URL || 'http://localhost:3000';

// Check if all required environment variables are set
const isConfigValid = Boolean(ODOO_API_URL && ODOO_DB && ODOO_USERNAME);

// Flag to use mock data only in development environment
const useMockData = false; // Disabled completely - only use real data

// Sample mock data for development and testing
const MOCK_SHIPMENTS = {
  // Tracking numbers often scanned
  '231113-050433': { id: 1001, name: 'Envío de Technosun #1', carrier_tracking_ref: '231113-050433' },
  '123456789': { id: 1002, name: 'Envío de Technosun #2', carrier_tracking_ref: '123456789' },
  'TRACK001': { id: 1003, name: 'Envío de Technosun #3', carrier_tracking_ref: 'TRACK001' },
  
  // Add more test tracking numbers
  'TN12345678': { id: 1004, name: 'Envío con Tracking TN12345678', carrier_tracking_ref: 'TN12345678' },
  '987654321': { id: 1005, name: 'Envío con Tracking 987654321', carrier_tracking_ref: '987654321' },
  'ABC123': { id: 1006, name: 'Envío con Tracking ABC123', carrier_tracking_ref: 'ABC123' },
  'XYZ789': { id: 1007, name: 'Envío con Tracking XYZ789', carrier_tracking_ref: 'XYZ789' },
  
  // Add common carrier formats
  'DHL12345678': { id: 1008, name: 'Envío DHL', carrier_tracking_ref: 'DHL12345678' },
  '1Z12345E0205271688': { id: 1009, name: 'Envío UPS', carrier_tracking_ref: '1Z12345E0205271688' },
  '390535363574': { id: 1010, name: 'Envío FedEx', carrier_tracking_ref: '390535363574' },
  'RM123456789ES': { id: 1011, name: 'Envío Correos', carrier_tracking_ref: 'RM123456789ES' },
};

/**
 * Find a shipment in Odoo by tracking number
 * @param {string} trackingNumber - The tracking number to search for
 * @returns {Promise<Object|null>} - The shipment data or null if not found
 */
export async function findShipmentByTrackingNumber(trackingNumber) {
  try {
    console.log(`Searching for shipment with tracking number: ${trackingNumber}`);
    
    // Use mock data for development or when API is not configured
    if (useMockData) {
      console.log('Using mock data for development/testing');
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Find shipment in mock data
      const mockShipment = MOCK_SHIPMENTS[trackingNumber];
      
      if (mockShipment) {
        console.log(`Found mock shipment: `, mockShipment);
        return mockShipment;
      } else {
        console.log(`No mock shipment found with tracking number: ${trackingNumber}`);
        return null;
      }
    }
    
    // Real API call for production
    try {
      // Determinar si estamos en producción o desarrollo
      const isProduction = window.location.hostname.includes('netlify.app');
      
      // Usar proxy server para evitar problemas de CORS en producción
      const apiUrl = isProduction 
        ? `${PROXY_URL}/api/find_shipment` 
        : `${ODOO_API_URL}/api/find_shipment`;
      
      console.log(`Usando URL para buscar envío: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tracking_number: trackingNumber,
          carrier_tracking_ref: trackingNumber,
          db: ODOO_DB,
          login: ODOO_USERNAME
        })
      });

      // Log response details for debugging
      console.log(`API response status: ${response.status}`);
      
      if (!response.ok) {
        // Try to parse as JSON first
        const text = await response.text();
        let errorMessage = 'Error finding shipment';
        
        // Handle specific HTTP status codes
        if (response.status === 404) {
          errorMessage = 'API endpoint not found. Please check API configuration.';
        } else if (response.status === 403) {
          errorMessage = 'Access denied to API. Please check API credentials.';
        } else if (response.status === 401) {
          errorMessage = 'Unauthorized access to API. Please check authentication.';
        } else if (response.status === 429) {
          errorMessage = 'Too many requests to API. Please try again later.';
        } else if (response.status >= 500) {
          errorMessage = 'API server error. Please contact system administrator.';
        }
        
        // Try to get more details from response if possible
        try {
          // See if we can parse the text as JSON
          if (text && text.trim() !== '') {
            const errorData = JSON.parse(text);
            if (errorData.error) {
              errorMessage = `${errorMessage}: ${errorData.error}`;
            }
          }
        } catch (jsonError) {
          // If not JSON, just use our status-based message
        }
        
        throw new Error(errorMessage);
      }

      // Safely parse JSON response
      const text = await response.text();
      
      // Handle empty responses
      if (!text || text.trim() === '') {
        console.log('Received empty response from API');
        return null;
      }
      
      const data = JSON.parse(text);
      
      // Log successful search
      if (data.result) {
        console.log(`Found shipment with ID: ${data.result.id}`);
        return data.result;
      } else {
        console.log(`No shipment found with tracking number: ${trackingNumber}`);
        throw new Error(`Tracking no encontrado: ${trackingNumber}`);
      }
    } catch (apiError) {
      console.error('API request failed:', apiError);
      
      // Special handling for CORS errors
      if (apiError.message && apiError.message.includes('NetworkError') ||
          apiError.message && apiError.message.includes('Failed to fetch')) {
        // This is likely a CORS error
        const corsErrorMessage = `API connection error: CORS policy preventing access to ${ODOO_API_URL}. 
        This application cannot access the Odoo server from this domain. 
        Please contact your administrator to configure CORS or implement a proxy server.`;
        
        console.error(corsErrorMessage);
        throw new Error('Error de conexión a API: Política CORS bloqueando el acceso');
      }
      
      // Disabled mock data references - only using real data
      if (import.meta.env.DEV) {
        console.log('API request failed. No mock data will be used.');
      }
      
      throw apiError;
    }
  } catch (error) {
    console.error('Error finding shipment:', error);
    throw error;
  }
}

/**
 * Update the pickup datetime for a shipment in Odoo
 * @param {number} shipmentId - The Odoo ID of the shipment
 * @param {string} datetime - ISO datetime string
 * @returns {Promise<Object>} - Result of the update operation
 */
export async function updateShipmentPickupDatetime(shipmentId, datetime) {
  try {
    console.log(`Updating shipment ${shipmentId} with pickup time: ${datetime}`);
    
    // Use mock data for development or when API is not configured
    if (useMockData) {
      console.log('Using mock data for development/testing (update operation)');
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mock successful update
      console.log(`Mock update successful for shipment ID: ${shipmentId}`);
      return { success: true, id: shipmentId };
    }
    
    // Real API call for production
    try {
      // Determinar si estamos en producción o desarrollo
      const isProduction = window.location.hostname.includes('netlify.app');
      
      // Usar proxy server para evitar problemas de CORS en producción
      const apiUrl = isProduction 
        ? `${PROXY_URL}/api/update_pickup_datetime` 
        : `${ODOO_API_URL}/api/update_pickup_datetime`;
      
      console.log(`Usando URL para actualizar envío: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shipment_id: shipmentId,
          pickup_datetime: datetime,
          db: ODOO_DB,
          login: ODOO_USERNAME
        })
      });

      // Log response details for debugging
      console.log(`API update response status: ${response.status}`);
      
      if (!response.ok) {
        // Try to parse as JSON first
        const text = await response.text();
        let errorMessage = 'Error updating shipment';
        
        // Handle specific HTTP status codes
        if (response.status === 404) {
          errorMessage = 'API update endpoint not found. Please check API configuration.';
        } else if (response.status === 403) {
          errorMessage = 'Access denied to API update. Please check API credentials.';
        } else if (response.status === 401) {
          errorMessage = 'Unauthorized access to API update. Please check authentication.';
        } else if (response.status === 429) {
          errorMessage = 'Too many requests to API. Please try again later.';
        } else if (response.status >= 500) {
          errorMessage = 'API server error during update. Please contact system administrator.';
        }
        
        // Try to get more details from response if possible
        try {
          // See if we can parse the text as JSON
          if (text && text.trim() !== '') {
            const errorData = JSON.parse(text);
            if (errorData.error) {
              errorMessage = `${errorMessage}: ${errorData.error}`;
            }
          }
        } catch (jsonError) {
          // If not JSON, just use our status-based message
        }
        
        throw new Error(errorMessage);
      }

      // Safely parse JSON response
      const text = await response.text();
      
      // Handle empty responses
      if (!text || text.trim() === '') {
        console.log('Received empty response from update API');
        return { success: true, id: shipmentId };
      }
      
      try {
        const data = JSON.parse(text);
        return data.result || { success: true, id: shipmentId };
      } catch (parseError) {
        console.error('Failed to parse API update response:', parseError);
        // Assume success if we can't parse the response but status was OK
        return { success: true, id: shipmentId };
      }
    } catch (apiError) {
      console.error('API update request failed:', apiError);
      
      // Special handling for CORS errors
      if (apiError.message && apiError.message.includes('NetworkError') ||
          apiError.message && apiError.message.includes('Failed to fetch')) {
        // This is likely a CORS error
        const corsErrorMessage = `API update connection error: CORS policy preventing access to ${ODOO_API_URL}. 
        This application cannot access the Odoo server from this domain. 
        Please contact your administrator to configure CORS or implement a proxy server.`;
        
        console.error(corsErrorMessage);
        throw new Error('Error de conexión a API: Política CORS bloqueando el acceso');
      }
      
      // Disabled mock data references - only using real data
      if (import.meta.env.DEV) {
        console.log('API update request failed. No mock data will be used.');
      }
      
      throw apiError;
    }
  } catch (error) {
    console.error('Error updating shipment:', error);
    throw error;
  }
}